import React, { useEffect, useState } from 'react'
import styles from './CustomerCardSettings.module.scss'
import asideModalStore from 'Stores/asideModalStore'
import FormField from 'components/UI/AsideModal/Form/FormField'
import SelectCustom from 'components/ContentBox/Global/Select/SelectCustom'
import { observer } from 'mobx-react'
import FormInput from 'components/UI/AsideModal/Form/FormInput'
import { ReactComponent as TrashIcon } from 'components/UI/icons/delete-bin-6-line.svg'
import { superAxiosRequest } from 'axiosApi'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import useLocalization from 'hooks/useLocalization'

const SettingDealFields = ({ dealId, funnelId }) => {
  const { modalValues: fields, changeValues, check } = asideModalStore

  const [showNewField, setShowNewField] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isFieldsFilled, setIsFieldsFilled] = useState(false)

  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'crm_settings',
    dict: {
      add_field: 'Add a field',
      field_name: 'Field name',
      field_type: 'Field type',
      boolean: 'Boolean',
      text: 'Text',
      calendar: 'Calendar',
      required_field: 'Required',
    },
  })

  const initOptions = [
    { id: 1, label: translate.text, name: 'string' },
    { id: 2, label: translate.boolean, name: 'boolean' },
    { id: 3, label: translate.calendar, name: 'calendar' },
  ]

  const fetchFields = () => {
    setLoading(true)
    superAxiosRequest({
      link: `integration/additional_funnel_deal_fields`,
      method: 'get',
      crm: true,
      params: {
        funnel_id: +funnelId,
      },
    })
      .then(({ data }) => {
        const fieldsWithKeys = data.map((field) => ({
          ...field,
          tempKey: field.id,
          is_required: field.is_required || false,
        }))
        changeValues({ additional_funnel_deal_fields: fieldsWithKeys })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchFields()
  }, [])

  useEffect(() => {
    setIsFieldsFilled(fields.name && fields.type)
  }, [fields.name, fields.type])

  const handleFieldChange = (tempKey, fieldName, value) => {
    const updatedFields = fields.additional_funnel_deal_fields.map((field) => {
      if (field.tempKey === tempKey) {
        return { ...field, [fieldName]: value }
      }
      return field
    })
    changeValues({ additional_funnel_deal_fields: updatedFields })
  }

  const generateUniqueId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  const addField = () => {
    if (isFieldsFilled) {
      const typeMap = {
        1: 'string',
        2: 'boolean',
        3: 'calendar',
      }

      const fieldType = typeMap[fields.type] || 'string'

      const newField = {
        tempKey: generateUniqueId(),
        name: fields.name || '',
        type: fieldType,
        funnel: funnelId,
        is_required: fields.is_required || false,
      }

      const updatedFields = [newField, ...fields.additional_funnel_deal_fields]

      changeValues({
        ...fields,
        additional_funnel_deal_fields: updatedFields,
        name: '',
        type: null,
        is_required: false,
      })

      setShowNewField(false)
      setIsFieldsFilled(false)
    }
  }

  const deleteField = (tempKey) => {
    const updatedFields = fields.additional_funnel_deal_fields.filter(
      (field) => field.tempKey !== tempKey,
    )
    changeValues({
      ...fields,
      additional_funnel_deal_fields: updatedFields,
    })
  }

  const CheckboxField = ({ checked, onChange, label }) => (
    <label className={styles.checkboxLabel}>
      <span className={styles.checkboxText}>{label}</span>

      <input
        type='checkbox'
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className={styles.checkbox}
      />
    </label>
  )

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className={styles.groupForm}>
          {!isFieldsFilled &&
            fields?.additional_funnel_deal_fields?.length <= 39 && (
              <div
                className={styles.addNewField}
                onClick={() => setShowNewField(true)}
              >
                {translate.add_field}
              </div>
            )}
          {isFieldsFilled && (
            <button className={styles.addNewField} onClick={addField}>
              Add New Field
            </button>
          )}
          {showNewField && (
            <div className={styles.field}>
              <div className={styles.input}>
                <FormField>
                  <FormInput isError={check('name')}>
                    <input
                      placeholder={translate.field_name}
                      type='text'
                      value={fields.name || ''}
                      onChange={(e) => {
                        changeValues({ ...fields, name: e.target.value })
                      }}
                    />
                  </FormInput>
                </FormField>
              </div>
              <div className={styles.select}>
                <FormField>
                  <SelectCustom
                    placeholder={translate.field_type}
                    options={initOptions}
                    sortKey='label'
                    valueID={fields.type}
                    onChange={(o) => {
                      changeValues({ ...fields, type: o.id })
                    }}
                    error={check('type')}
                  />
                </FormField>
              </div>
              {/*<div className={`${styles.trashIcon} ${styles.disabled}`}>*/}
              {/*  <TrashIcon />*/}
              {/*</div>*/}
              <div className={styles.checkboxWrapper}>
                <CheckboxField
                  checked={fields.is_required || false}
                  onChange={(checked) =>
                    changeValues({ ...fields, is_required: checked })
                  }
                  label={translate.required_field}
                />
              </div>
            </div>
          )}
          <div className={styles.fieldsContainer}>
            {fields?.additional_funnel_deal_fields?.map((field, index) => (
              <div key={field.tempKey} className={styles.field}>
                <div className={styles.input}>
                  <FormField>
                    <FormInput>
                      <input
                        placeholder={translate.field_name}
                        type='text'
                        value={field.name || ''}
                        onChange={(e) =>
                          handleFieldChange(
                            field.tempKey,
                            'name',
                            e.target.value,
                          )
                        }
                      />
                    </FormInput>
                  </FormField>
                </div>
                <div className={styles.select}>
                  <FormField>
                    <SelectCustom
                      placeholder='Field type'
                      options={initOptions}
                      sortKey='label'
                      valueID={
                        field.type === 'string'
                          ? 1
                          : field.type === 'boolean'
                            ? 2
                            : 3
                      }
                      onChange={(o) =>
                        handleFieldChange(field.tempKey, 'type', o.name)
                      }
                      isDisabled
                    />
                  </FormField>
                </div>
                <div
                  className={styles.trashIcon}
                  onClick={() => deleteField(field.tempKey)}
                >
                  <TrashIcon />
                </div>
                <div className={styles.checkboxWrapper}>
                  <CheckboxField
                    checked={field.is_required || false}
                    onChange={(checked) =>
                      handleFieldChange(field.tempKey, 'is_required', checked)
                    }
                    label={translate.required_field}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default observer(SettingDealFields)
